import axios from "axios";
import { tokenUtils } from "../../security/index";

const API_BASE_URL =
  import.meta.env.MODE === "development"
    ? "http://localhost:8000/"
    : import.meta.env.VITE_API_BASE_URL;
let refreshPromise = null; // ← Make sure this is declared OUTSIDE the interceptor

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 seconds default for regular requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = tokenUtils.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // config.headers["X-Request-Time"] = Date.now();

    // Log request in development
    if (import.meta.env.MODE === "development") {
      console.log(API_BASE_URL);
      console.log(
        `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`,
        {
          data: config.data,
          params: config.params,
          headers: config.headers,
        }
      );
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle network errors (no response from server)
    if (!error.response) {
      const friendlyError = new Error("Something went wrong, please contact your administrator.");
      friendlyError.isNetworkError = true;
      friendlyError.originalError = error;
      return Promise.reject(friendlyError);
    }

    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        const refreshToken = tokenUtils.getRefreshToken();

        if (!refreshToken) {
          tokenUtils.clearTokens();
          if (!window.location.pathname.startsWith("/login")) {
            window.location.href = "/login";
          }
          return Promise.reject(error);
        }

        refreshPromise = axios
          .post(`${API_BASE_URL}/api/auth/jwt/refresh/`, {
            refresh: refreshToken,
          })
          .then((res) => {
            tokenUtils.setAccessToken(res.data.access);
            if (res.data.refresh) {
              tokenUtils.setRefreshToken(res.data.refresh);
            }
            return res.data.access;
          })
          .catch((refreshError) => {
            console.error("💥 [Refresh Failed]", {
              status: refreshError.response?.status,
              data: refreshError.response?.data,
              message: refreshError.message,
            });
            throw refreshError; // Re-throw so it goes to the catch below
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      try {
        const newAccess = await refreshPromise;

        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        tokenUtils.clearTokens();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Export axios instance and config
export { axiosInstance, API_BASE_URL };
export default axiosInstance;
